import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography, Grid } from "@mui/material";
import { useTheme, lighten } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  CounterDetailsContainer,
  CounterDetail,
  CounterDetailDays,
  CounterDetailTitle,
  BalanceContainer,
  SegmentsContainer,
  Segment,
} from "./styles";
import { COLORS } from "../../../_globalStore/redux/reducers/Theme/themeConstants";
import { TnAMobileHoursBalanceDTO } from "../../../models/tna/tna-mobile-hours/tna-mobile-hours-balance-dto";

interface Props {
  mobileHours: TnAMobileHoursBalanceDTO;
  smallSize?: boolean;
}

const TnAMobileHours = ({ mobileHours, smallSize = false }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.my_calendar";
  const totalHours =
    Math.abs(mobileHours.fromPreviousMonths) +
    Math.abs(mobileHours.workedThisMonth) +
    Math.abs(mobileHours.dueThisMonth) +
    Math.abs(mobileHours.workedVSDueThisMonth) +
    mobileHours.correctionThisMonth;

  return (
    <Container>
      <Grid
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          width: "80%",
        }}
      >
        <SegmentsContainer>
          {mobileHours.fromPreviousMonths !== 0 && (
            <Segment
              style={{
                backgroundColor: COLORS.yellow_gradients.yellow,
                width: `${(Math.abs(mobileHours.fromPreviousMonths) / totalHours) * 100}%`,
              }}
            />
          )}
          {mobileHours.workedThisMonth !== 0 && (
            <Segment
              style={{
                backgroundColor: theme.palette.primary.light,
                width: `${(Math.abs(mobileHours.workedThisMonth) / totalHours) * 100}%`,
              }}
            />
          )}
          {mobileHours.dueThisMonth !== 0 && (
            <Segment
              style={{
                backgroundColor: COLORS.green_gradients.green,
                width: `${(Math.abs(mobileHours.dueThisMonth) / totalHours) * 100}%`,
              }}
            />
          )}
          {mobileHours.workedVSDueThisMonth !== 0 && (
            <Segment
              style={{
                backgroundColor: lighten(theme.palette.error.light, 0.3),
                width: `${(Math.abs(mobileHours.workedVSDueThisMonth) / totalHours) * 100}%`,
              }}
            />
          )}
          {mobileHours.correctionThisMonth !== 0 && (
            <Segment
              style={{
                backgroundColor: lighten(theme.palette.secondary.light, 0.3),
                width: `${(Math.abs(mobileHours.correctionThisMonth) / totalHours) * 100}%`,
              }}
            />
          )}
        </SegmentsContainer>

        <CounterDetailsContainer>
          <CounterDetail>
            <CounterDetailTitle>
              <FontAwesomeIcon
                icon={faCircle}
                style={{ color: COLORS.yellow_gradients.yellow }}
              />
              <Typography>{t(`${tRP}.fields.from_previous_months`)}</Typography>
            </CounterDetailTitle>
            <CounterDetailDays>
              <Typography variant={smallSize ? "h5" : "h4"}>{`${Number(mobileHours.fromPreviousMonths).toFixed(
                2
              )} `}</Typography>
            </CounterDetailDays>
            <Typography variant="body2">{t(`${tRP}.hours`)}</Typography>
          </CounterDetail>

          <CounterDetail>
            <CounterDetailTitle>
              <FontAwesomeIcon
                icon={faCircle}
                style={{ color: theme.palette.primary.light }}
              />
              <Typography>{t(`${tRP}.fields.worked_this_month`)}</Typography>
            </CounterDetailTitle>
            <CounterDetailDays>
              <Typography variant={smallSize ? "h5" : "h4"}>{`${Number(mobileHours.workedThisMonth).toFixed(
                2
              )} `}</Typography>
            </CounterDetailDays>
            <Typography variant="body2">{t(`${tRP}.hours`)}</Typography>
          </CounterDetail>

          <CounterDetail>
            <CounterDetailTitle>
              <FontAwesomeIcon
                icon={faCircle}
                style={{ color: COLORS.green_gradients.green }}
              />
              <Typography>{t(`${tRP}.fields.due_this_month`)}</Typography>
            </CounterDetailTitle>
            <CounterDetailDays>
              <Typography variant={smallSize ? "h5" : "h4"}>{`${Number(mobileHours.dueThisMonth).toFixed(
                2
              )} `}</Typography>
            </CounterDetailDays>
            <Typography variant="body2">{t(`${tRP}.hours`)}</Typography>
          </CounterDetail>

          <CounterDetail>
            <CounterDetailTitle>
              <FontAwesomeIcon
                icon={faCircle}
                style={{ color: lighten(theme.palette.error.light, 0.3) }}
              />
              <Typography>{t(`${tRP}.fields.worked_vs_due_this_month`)}</Typography>
            </CounterDetailTitle>
            <CounterDetailDays>
              <Typography variant={smallSize ? "h5" : "h4"}>{`${Number(mobileHours.workedVSDueThisMonth).toFixed(
                2
              )} `}</Typography>
            </CounterDetailDays>
            <Typography variant="body2">{t(`${tRP}.hours`)}</Typography>
          </CounterDetail>

          <CounterDetail>
            <CounterDetailTitle>
              <FontAwesomeIcon
                icon={faCircle}
                style={{ color: lighten(theme.palette.secondary.light, 0.3) }}
              />
              <Typography>{t(`${tRP}.fields.correction_this_month`)}</Typography>
            </CounterDetailTitle>
            <CounterDetailDays>
              <Typography variant={smallSize ? "h5" : "h4"}>
                {`${Number(mobileHours.correctionThisMonth).toFixed(2)} `}
              </Typography>
            </CounterDetailDays>
            <Typography variant="body2">{t(`${tRP}.hours`)}</Typography>
          </CounterDetail>
        </CounterDetailsContainer>
      </Grid>

      <BalanceContainer>
        <CounterDetailTitle style={{ justifyContent: "center" }}>
          <Typography style={{ textAlign: "center" }}>{t(`${tRP}.fields.total_worked_vs_due_balance`)}</Typography>
        </CounterDetailTitle>
        <CounterDetailDays>
          <Typography variant={smallSize ? "h5" : "h4"}>{`${Number(mobileHours.totalWorkedVSDueBalance).toFixed(
            2
          )} `}</Typography>
        </CounterDetailDays>
        <Typography variant="body2">{t(`${tRP}.hours`)}</Typography>
      </BalanceContainer>
    </Container>
  );
};

export default TnAMobileHours;
